import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  RadioGroup,
  Radio,
  Paper,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { useLogger } from "../../../../contexts/LoggerContext";
import { GameSituation } from "../../../../types/LogTypes";
import { GameClockInput } from "./GameClockInput";
import { YardlineInput } from "./YardlineInput";

interface TeamInfoCardProps {
  logo?: string;
  name: string;
  score: number;
  timeouts: number;
  onScoreChange: (value: number) => void;
  onTimeoutChange: (value: number) => void;
}

const TeamInfoCard: React.FC<TeamInfoCardProps> = ({
  logo,
  name,
  score,
  timeouts,
  onScoreChange,
  onTimeoutChange,
}) => {
  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {logo && (
          <Box
            component="img"
            src={logo}
            alt={name}
            sx={{
              height: 40,
              width: 40,
              objectFit: "contain",
              borderRadius: "4px",
            }}
          />
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">{name}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">Score:</Typography>
            <TextField
              size="small"
              type="number"
              value={score}
              onChange={(e) => onScoreChange(parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, style: { width: "50px" } }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="body2">Timeouts:</Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {[...Array(3)].map((_, i) =>
                i < timeouts ? (
                  <CircleIcon
                    key={i}
                    sx={{ fontSize: 16, color: "primary.main" }}
                  />
                ) : (
                  <CircleOutlinedIcon
                    key={i}
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                )
              )}
            </Box>
            <TextField
              size="small"
              type="number"
              value={timeouts}
              onChange={(e) => onTimeoutChange(parseInt(e.target.value) || 0)}
              inputProps={{ min: 0, max: 3, style: { width: "50px" } }}
              sx={{ ml: 1 }}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export const EditScoreBoardDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { gameSituation, setGameSituation, selectedGame } = useLogger();
  const clubLogo = selectedGame?.user_club?.profile_picture;
  const oppClubLogo = selectedGame?.opponent_club?.profile_picture;
  const clubName = selectedGame?.user_club?.name;
  const oppClubName = selectedGame?.opponent_club?.name;

  const handleChange =
    (field: keyof GameSituation) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value;

      if (field === "club_possession") {
        // For radio buttons, value will be "true" or "false" as a string
        value = event.target.value === "true";
      } else if (event.target.type === "number") {
        value = parseInt(event.target.value) || 0;
      } else {
        value = event.target.value;
      }

      setGameSituation({
        ...gameSituation,
        [field]: value,
      });
    };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Scoreboard</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            {/* Top Row: Game Progress and Game Situation */}
            <Grid item xs={12} container spacing={2}>
              {/* Game Progress */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box>
                    <TextField
                      size="small"
                      label="Quarter"
                      type="number"
                      value={gameSituation.quarter}
                      onChange={handleChange("quarter")}
                      inputProps={{
                        min: 1,
                        max: 4,
                        style: { width: "70px", textAlign: "center" },
                      }}
                    />
                    </Box>
                    <Box
                    sx={{
                      marginTop: 2
                    }}
                    >
                    <GameClockInput />
                  </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Game Situation */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                      marginTop: 7,
                      marginBottom: 3,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                    >
                      <TextField
                        size="small"
                        label="Down"
                        type="number"
                        value={gameSituation.down}
                        onChange={handleChange("down")}
                        inputProps={{
                          min: 1,
                          max: 4,
                          style: { width: "50px", textAlign: "center" },
                        }}
                      />
                      <TextField
                        size="small"
                        label="Distance"
                        type="number"
                        value={gameSituation.distance}
                        onChange={handleChange("distance")}
                        inputProps={{
                          min: 1,
                          style: { width: "50px", textAlign: "center" },
                        }}
                      />
                    </Box>
                    <YardlineInput
                      label="Ball On"
                      value={gameSituation.yardline}
                      onChange={(value) => {
                        setGameSituation({
                          ...gameSituation,
                          yardline: value,
                        });
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Team Info Cards */}
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} md={6}>
                <TeamInfoCard
                  logo={clubLogo}
                  name={clubName || "Home Team"}
                  score={gameSituation.club_score}
                  timeouts={gameSituation.club_tol}
                  onScoreChange={(value) =>
                    setGameSituation({
                      ...gameSituation,
                      club_score: value,
                    })
                  }
                  onTimeoutChange={(value) =>
                    setGameSituation({
                      ...gameSituation,
                      club_tol: value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TeamInfoCard
                  logo={oppClubLogo}
                  name={oppClubName || "Away Team"}
                  score={gameSituation.opp_score}
                  timeouts={gameSituation.opp_tol}
                  onScoreChange={(value) =>
                    setGameSituation({
                      ...gameSituation,
                      opp_score: value,
                    })
                  }
                  onTimeoutChange={(value) =>
                    setGameSituation({
                      ...gameSituation,
                      opp_tol: value,
                    })
                  }
                />
              </Grid>
            </Grid>

            {/* Possession Section */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Possession
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <RadioGroup
                    row
                    value={gameSituation.club_possession.toString()}
                    onChange={handleChange("club_possession")}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {clubLogo && (
                          <Box
                            component="img"
                            src={clubLogo}
                            alt={clubName}
                            sx={{
                              height: 30,
                              width: 30,
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                        <Radio value="true" />
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {oppClubLogo && (
                          <Box
                            component="img"
                            src={oppClubLogo}
                            alt={oppClubName}
                            sx={{
                              height: 30,
                              width: 30,
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                        <Radio value="false" />
                      </Box>
                    </Box>
                  </RadioGroup>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
